import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';

interface SaveCommentDialogProps {
    open: boolean;
    isComplete: boolean;
    onConfirm: (
        mode: 'complete' | 'continue' | 'close',
        comment?: string
    ) => void;
    onCancel: () => void;
}

export function SaveCommentDialog({
    open,
    isComplete,
    onConfirm,
    onCancel,
}: SaveCommentDialogProps) {
    const { t } = useTranslation();
    const [comment, setComment] = useState('');

    const handleConfirm = (mode: 'complete' | 'continue' | 'close') => {
        const trimmed = comment.trim();
        onConfirm(mode, trimmed || undefined);
        setComment('');
    };

    const handleCancel = () => {
        setComment('');
        onCancel();
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && handleCancel()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {isComplete ? t('departmentPolicies.saveDialog.completeTitle') : t('departmentPolicies.saveDialog.saveTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {isComplete
                            ? t('departmentPolicies.saveDialog.completeDescription')
                            : t('departmentPolicies.saveDialog.saveDescription')}
                    </DialogDescription>
                </DialogHeader>
                <textarea
                    placeholder={t('departmentPolicies.saveDialog.commentPlaceholder')}
                    value={comment}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
                    rows={3}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                />
                <DialogFooter>
                    <Button variant="outline" onClick={handleCancel}>
                        {t('departmentPolicies.saveDialog.cancel')}
                    </Button>
                    {isComplete ? (
                        <Button onClick={() => handleConfirm('complete')}>
                            {t('departmentPolicies.saveDialog.complete')}
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => handleConfirm('continue')}
                            >
                                {t('departmentPolicies.saveDialog.saveContinue')}
                            </Button>
                            <Button onClick={() => handleConfirm('close')}>
                                {t('departmentPolicies.saveDialog.saveClose')}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
